<template>
  <nav class="flex flex-col bg-white border-r border-gray-200" data-testid="deck-sidebar">
    <div class="border-b border-gray-200 p-6 pt-0">
      <a
        ref="companyLogo"
        class="mx-auto mb-2 p-1 max-w-full cursor-pointer"
        :href="companyPageUrl"
        target="_blank"
        data-testid="deck-slides-company-logo"
      >
        <img
          v-if="deck.company.logo"
          class="block mx-auto max-h-20 object-contain"
          :src="deck.company.logo"
          :alt="deck.company.name"
          :width="logoDimensions.width"
          :height="logoDimensions.height"
          draggable="false"
        />
      </a>
      <h4 class="text-ellipsis-2 mb-0 normal-case text-xl tracking-normal" :title="deck.title">{{ deck.title }}</h4>
      <div v-if="(showAAQButton || isBookAMeetingEnabled) && !user.deckUserType" class="flex justify-between mt-4">
        <VButton
          v-if="showAAQButton"
          class="flex items-center"
          data-testid="deck-slides-aaq-btn"
          component="button"
          size="xs"
          @click="$emit('on-aaq-clicked')"
        >
          <ChatBubbleLeftRightIcon class="mr-1.5 w-3 h-3" />Ask a Question</VButton
        >
        <VButton
          v-if="isBookAMeetingEnabled"
          v-analytics="'bookAMeetingClicked'"
          class="flex items-center"
          data-testid="deck-slides-bam-btn"
          component="a"
          variant="dark"
          size="xs"
          :href="`/meetings/book/${deck.request_setting_id}`"
          target="_blank"
          analytics-event-name="BookAMeeting"
          location="deck-sidebar"
        >
          <CalendarIcon class="mr-1.5 w-3 h-3" />Book a Meeting</VButton
        >
      </div>
    </div>
    <div ref="slidesContainer" class="slides-container">
      <ol class="mb-0">
        <DeckTile
          v-for="(slide, index) in deck.slides"
          :key="slide.id"
          :slide="slide"
          :index="index"
          :selected="index === currentSlideIndex"
          :disabled="slide.disabled"
          @on-slide-selected="handleSlideSelect"
        />
      </ol>
    </div>
    <div v-if="!nonShareableMeeting && shareCode" class="box-shadow text-center text-gray-700 p-4">
      <p class="select-text text-sm mb-2" data-testid="slides-list-share-code">Share Code: {{ formatShareCode(shareCode) }}</p>
      <VCopyButton class="w-full flex justify-center items-center" variant="copper-outline" size="xs" :copy-text="copyText">
        <DuplicateIcon class="w-5 mr-2 -mt-1 -mb-0.5" />Copy Join Info
      </VCopyButton>
    </div>
  </nav>
</template>

<script lang="ts" setup>
import type { Numberish } from '@/entities/index'
import type { Deck, DeckUser, IncludeCompany } from '@/entities/deck'
import { ASKABLE_SLIDES } from '@/entities/deck'
import { ChatBubbleLeftRightIcon, CalendarIcon } from '@heroicons/vue/24/solid'
import DuplicateIcon from '@/components/icons/DuplicateIcon.svg'
import type { DeckWithEmbed } from '@/entities/embed'
import type { Meeting } from '@/entities/meeting'
import type { DeepReadonly } from 'vue'

const MAX_LOGO_HEIGHT = 128

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<Deck & IncludeCompany>>,
    required: true,
  },
  meeting: {
    type: Object as PropType<DeepReadonly<Meeting>>,
    default: undefined,
  },
  currentSlideIndex: {
    type: Number,
    required: true,
  },
  analyticsEventName: {
    type: String,
    required: true,
  },
  uuid: {
    type: String,
    default: null,
  },
  user: {
    type: Object as PropType<DeepReadonly<DeckUser>>,
    default: () => ({}),
  },
  shareCode: {
    type: String,
    default: null,
  },
  nonShareableMeeting: {
    type: Boolean,
    default: false,
  },
  isSidebarOpen: {
    type: Boolean,
    required: true,
  },
  embedDeck: {
    type: Object as PropType<DeepReadonly<DeckWithEmbed>>,
    default: null,
  },
})

const emit = defineEmits(['on-aaq-clicked', 'set-current-slide-index', 'start-auto-play'])

const { $analytics } = useNuxtApp()

const companyLogo: Ref<HTMLElement | null> = ref(null)
const slidesContainer: Ref<Element | null> = ref(null)

const { copyText } = useShareMeetingLink({
  shareCode: toRef(props, 'shareCode'),
  user: toRef(props, 'user'),
  conference: toRef(props, 'meeting'),
})

const isBookAMeetingEnabled = computed(() => {
  const { show_bam_sidebar_button, request_setting } = props.deck
  return !!(show_bam_sidebar_button && request_setting?.able_to_book)
})

const showAAQButton = computed(() => {
  return props.deck.show_aaq_button && ASKABLE_SLIDES.includes(props.deck.slides?.[props.currentSlideIndex]?.type)
})

const logoDimensions: ComputedRef<{ width: Numberish | undefined; height: Numberish | undefined }> = computed(() => {
  const logoFile = props.deck.company.logo_file
  if (!logoFile) return { width: undefined, height: undefined }
  const height = Math.min(MAX_LOGO_HEIGHT, logoFile.height)
  return {
    width: height * (logoFile.width / logoFile.height),
    height: height,
  }
})

watch(() => props.currentSlideIndex, _scrollSelectedSlideIntoView)
watch(
  () => props.isSidebarOpen,
  (isSidebarOpen: boolean) => isSidebarOpen && _scrollSelectedSlideIntoView()
)

onMounted(_scrollSelectedSlideIntoView)

function handleSlideSelect(index: number) {
  $analytics.track(`${props.analyticsEventName}SlideSelected`, {
    slideIndex: index,
    deckId: props.deck.id,
    deckName: props.deck.title,
    companyId: props.deck.company.id,
    companyName: props.deck.company.name,
    slideId: props.deck.slides[index].id,
    uuid: props.uuid,
    partnerId: props.embedDeck?.partner_id,
    partnerName: props.embedDeck?.partner_name,
    embedLocation: props.embedDeck?.embed_name,
  })

  emit('set-current-slide-index', index)
}

function formatShareCode(code: string): string {
  if (code.length <= 5) return code

  return code.match(/.{4}(?=.{2,4})|.+/g)?.join('-') ?? ''
}

function _scrollSelectedSlideIntoView() {
  if (!slidesContainer.value) return

  const index = props.currentSlideIndex
  const selected = slidesContainer.value.querySelectorAll('li')[index]

  if (!selected) return

  const isOnTopOfView = selected.offsetTop < slidesContainer.value.scrollTop
  const isBelowView =
    selected.offsetTop + selected.offsetHeight >
    slidesContainer.value.scrollTop + slidesContainer.value.clientHeight - selected.offsetHeight

  if (isOnTopOfView) {
    setTimeout(() => {
      slidesContainer.value?.scrollTo({ top: selected.offsetTop - 8, behavior: 'smooth' })
    })
  } else if (isBelowView) {
    setTimeout(() => {
      slidesContainer.value?.scrollTo({
        top: selected.offsetTop + selected.offsetHeight - slidesContainer.value.clientHeight + 8,
        left: 0,
        behavior: 'smooth',
      })
    })
  }
}
const companyPageUrl = computed(() => `/companies/${props.deck?.company?.id}`)
</script>

<style lang="stylus" scoped>
nav
  -webkit-touch-callout: none
  -webkit-user-select: none
  -khtml-user-select: none
  -moz-user-select: none
  -ms-user-select: none
  user-select: none

.box-shadow
  box-shadow: 0px -3px 24px rgba(225, 225, 225, 0.5);

.slides-container
  -webkit-overflow-scrolling: touch

  @apply h-full flex flex-col justify-between p-2 overflow-y-auto relative

.text-ellipsis-2
  word-break break-word
  display -webkit-box
  text-overflow ellipsis
  overflow hidden
  -webkit-box-orient vertical
  -webkit-line-clamp 2
</style>
